import type { Language } from '@eformulieren/engine'
import type { Side } from '@eformulieren/model'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { type AccessActions, type AccessData, AccessList } from './AccessList.js'
import { BusyButton } from './BusyButton.js'
import { CountBadge, countName } from './CountBadge.js'
import { ShareInvite } from './ShareInvite.js'
import { type AdviceOffer, type InvitationOutcome, type InviteRequest, type ShareSection, type ShareStart, groupAccess } from './shareModel.js'
import type { Bilingual } from '../language/text.js'

export type { AccessActions, AccessData, OpeningEntry } from './AccessList.js'
export * from './shareModel.js'

/**
 * De dialoog voor delen en advies vragen, één systeem met twee tabbladen (R-64, R-64a, R-65, R-65a,
 * R-65b, R-65c, R-65d, R-66, R-66a, R-66b, R-139, R-140, R-184, besluiten 1 tot en met 7 van
 * 30 oktober 2026, ontwerp 6.4 en 7.4a, mockups 01 tot en met 03 van delen en 02 tot en met 04 van
 * advies).
 *
 * **Twee tabbladen.** "Iemand uitnodigen" (`ShareInvite`) en "Wie heeft toegang" (`AccessList`). De
 * titel en de knop in de kop zeggen "Delen en advies vragen" aan de interne kant als de kijker advies
 * kan vragen (`advies` staat er, uit `kanVragen`), en anders "Delen"; wie niet mag delen (R-65d) krijgt
 * alleen de lijst, onder de titel "Wie heeft toegang".
 * Beide tabbladen blijven in de pagina terwijl de ander openstaat, zodat wat er al is ingevuld blijft
 * staan.
 *
 * **Openen met een anker.** Het adviespaneel en de tekstballon bij een vraag openen de dialoog met
 * `start` (`{ rol: 'advies', anker }`): dan is de rol ingeklapt en staat de focus op de vraag (A1).
 * "Deze sectie delen" geeft `{ secties: [id] }`; de lijst openen geeft `{ tab: 'toegang' }`.
 *
 * **Een vaste voet.** Annuleren en Uitnodiging versturen staan onder de inhoud en scrollen niet mee.
 * Tijdens het versturen zegt de knop "Bezig met versturen" (`BusyButton`), staan de velden stil, en
 * wachten Sluiten en Escape tot de pod antwoordt. Daarna gaat de dialoog naar Wie heeft toegang, met de
 * nieuwe regel bovenaan zijn groep en een melding in een `role=status` die altijd in de dialoog staat,
 * zodat een schermlezer haar voorleest zodra ze verschijnt.
 *
 * **Een modale dialoog met de focus erin.** `<dialog>` met `showModal()`, zodat de rest van de pagina
 * inert is; Tab en Shift+Tab lopen rond binnen de dialoog, Escape sluit, en na het sluiten staat de
 * focus weer waar hij was. Op een telefoon is de dialoog het hele scherm, met een vaste kop en voet
 * (mockup 03 van advies).
 */

export interface ShareDialogProps extends AccessActions {
  readonly taal: Language
  /** De kant van de kijker, uit de projectie: bepaalt de titel, Advies geven en de adviesregels. */
  readonly kant: Side
  /** De secties die de deler ziet, met wat hij er kan geven. */
  readonly secties: readonly ShareSection[]
  /** Of deze persoon mag uitnodigen (`magDelen` uit de projectie, R-64d, R-65d). */
  readonly canInvite: boolean
  /** Alleen als `GET /advies` `kanVragen` gaf: dan staat Advies geven erbij. */
  readonly advies?: AdviceOffer
  /** Hoe de dialoog opengaat; zie hierboven. */
  readonly start?: ShareStart
  /** De lijst van de eigen kant, of waar het ophalen staat. */
  readonly toegang: AccessData | 'laden' | 'mislukt'
  /** De titel van een sectie die de kijker heeft, in zijn taal; niets voor een sectie die hij niet heeft. */
  readonly sectionName: (sectionId: string) => string | undefined
  readonly onInvite: (request: InviteRequest) => Promise<InvitationOutcome>
  readonly onClose: () => void
  /**
   * De kijker kiest Wie heeft toegang, ook als dat tabblad al openstaat: lees de lijst opnieuw. Wie de
   * dialoog openhoudt terwijl de ander aanneemt, zag die anders nog als "Nog niet aangenomen" (R-66;
   * gevonden in de e2e-flow van meeschrijven, 30 oktober 2026).
   */
  readonly onShowAccess?: () => void
}

const T = {
  delen: { nl: 'Delen', en: 'Share' },
  delenAdvies: { nl: 'Delen en advies vragen', en: 'Share and ask for advice' },
  wieToegang: { nl: 'Wie heeft toegang', en: 'Who has access' },
  uitnodigen: { nl: 'Iemand uitnodigen', en: 'Invite someone' },
  sluiten: { nl: 'Sluiten', en: 'Close' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  versturen: { nl: 'Uitnodiging versturen', en: 'Send invitation' },
  bezig: { nl: 'Bezig met versturen…', en: 'Sending…' },
  verstuurd: {
    nl: 'De uitnodiging aan {email} is verstuurd. Die persoon krijgt een e-mail om haar aan te nemen; u ziet haar hieronder.',
    en: 'The invitation to {email} has been sent. They will receive an email to accept it; you can see it below.',
  },
} as const satisfies Readonly<Record<string, Bilingual>>

type Tab = 'uitnodigen' | 'toegang'

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'

export function ShareDialog(props: ShareDialogProps): ReactNode {
  const { taal: language, kant: side, canInvite, start, toegang: access, onClose } = props
  const modal = useRef<HTMLDialogElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const firstField = useRef<HTMLElement | null>(null)
  const tabRefs = { uitnodigen: useRef<HTMLButtonElement>(null), toegang: useRef<HTMLButtonElement>(null) }
  const headerId = useId()
  const baseId = useId()
  const formId = `${baseId}-uitnodigen`
  const [tab, setTab] = useState<Tab>(canInvite && start?.tab !== 'toegang' ? 'uitnodigen' : 'toegang')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')
  const [newId, setNewId] = useState<string | undefined>(undefined)
  /** Na versturen een vers formulier: een andere sleutel. */
  const [round, setRound] = useState(0)
  const busyRef = useRef(false)
  busyRef.current = busy

  /**
   * Openen als modale dialoog, en bij het weggaan de focus terug naar waar hij was. Wat de focus had
   * vóór het openen houdt het component zelf vast: zolang hij modaal open staat is de rest van de
   * pagina inert en kan daar niets de focus krijgen.
   */
  useEffect(() => {
    const element = modal.current
    if (element === null) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (!element.open) element.showModal()
    ;(tab === 'uitnodigen' ? (firstField.current ?? closeButton.current) : (tabRefs.toegang.current ?? closeButton.current))?.focus()
    return () => {
      if (element.open) element.close()
      previous?.focus()
    }
    // Alleen bij openen: de focus bij een tabwissel regelt de tabbalk.
  }, [])

  /** Naar het formulier vanuit de voet: de focus pas als het tabblad zichtbaar is. */
  const [focusForm, setFocusForm] = useState(false)
  useEffect(() => {
    if (!focusForm || tab !== 'uitnodigen') return
    firstField.current?.focus()
    setFocusForm(false)
  }, [focusForm, tab])

  /** Tab en Shift+Tab lopen rond binnen de dialoog, en gaan nooit naar de pagina erachter. */
  function keepFocusWithin(event: KeyboardEvent<HTMLDialogElement>): void {
    if (event.key !== 'Tab' || modal.current === null) return
    const within = Array.from(modal.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.offsetParent !== null)
    const first = within[0]
    const last = within.at(-1)
    if (first === undefined || last === undefined) return
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === modal.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function close(): void {
    if (busyRef.current) return
    onClose()
  }

  function chooseTab(next: Tab, focus: boolean): void {
    if (next === 'toegang') props.onShowAccess?.()
    setTab(next)
    if (focus) tabRefs[next].current?.focus()
  }

  /** Pijltjes, Home en End in de tabbalk (WAI-ARIA tabs, automatische activering). */
  function onTabKey(event: KeyboardEvent<HTMLButtonElement>): void {
    const order: readonly Tab[] = ['uitnodigen', 'toegang']
    const index = order.indexOf(tab)
    const next =
      event.key === 'ArrowRight' ? order[(index + 1) % order.length] : event.key === 'ArrowLeft' ? order[(index + order.length - 1) % order.length] : event.key === 'Home' ? order[0] : event.key === 'End' ? order.at(-1) : undefined
    if (next === undefined) return
    event.preventDefault()
    chooseTab(next, true)
  }

  const count =
    access === 'laden' || access === 'mislukt'
      ? undefined
      : groupAccess({ delingen: access.delingen, toekenningen: access.toekenningen, advies: side === 'intern' ? access.advies : [] }).toegang.length
  // Het aantal als vak naast het label en in de naam van de tab ("Wie heeft toegang: 2"), niet tussen haakjes.
  const accessLabel = (
    <>
      {T.wieToegang[language]}
      {count === undefined ? null : <CountBadge>{String(count)}</CountBadge>}
    </>
  )
  const accessName = count === undefined ? undefined : countName(T.wieToegang[language], [String(count)])
  const title = !canInvite ? T.wieToegang[language] : side === 'intern' && props.advies !== undefined ? T.delenAdvies[language] : T.delen[language]
  const ids = { tabInvite: `${baseId}-tab-uitnodigen`, tabAccess: `${baseId}-tab-toegang`, panelInvite: `${baseId}-paneel-uitnodigen`, panelAccess: `${baseId}-paneel-toegang` }

  const list = (
    <AccessList
      taal={language}
      kant={side}
      toegang={access}
      secties={props.secties}
      sectionName={props.sectionName}
      canInvite={canInvite}
      onWijzigen={props.onWijzigen}
      onIntrekken={props.onIntrekken}
      onOpnieuw={props.onOpnieuw}
      {...(props.onHerinneren === undefined ? {} : { onHerinneren: props.onHerinneren })}
      {...(props.onAdviesIntrekken === undefined ? {} : { onAdviesIntrekken: props.onAdviesIntrekken })}
      {...(props.onBekijkAdvies === undefined ? {} : { onBekijkAdvies: props.onBekijkAdvies })}
      {...(newId === undefined ? {} : { nieuw: newId })}
      onStatus={setStatus}
    />
  )

  return (
    <dialog
      ref={modal}
      className="ef-deeldialoog"
      aria-labelledby={headerId}
      aria-busy={busy ? 'true' : undefined}
      lang={language}
      onKeyDown={keepFocusWithin}
      onCancel={(event) => {
        // Escape: React houdt bij of de dialoog open is, dus de browser sluit hem niet zelf; tijdens
        // het versturen wacht hij.
        event.preventDefault()
        close()
      }}
    >
      <div className="ef-deeldialoog__kop">
        <div className="ef-deeldialoog__kopregel">
          <h2 id={headerId} className="ef-deeldialoog__titel">
            {title}
          </h2>
          <button ref={closeButton} type="button" className="ef-tekstknop ef-deeldialoog__sluiten" disabled={busy} onClick={close}>
            {T.sluiten[language]}
          </button>
        </div>
        {canInvite ? (
          <div role="tablist" className="ef-deeldialoog__tabs" aria-label={title}>
            {(['uitnodigen', 'toegang'] as const).map((option) => (
              <button
                key={option}
                ref={tabRefs[option]}
                id={option === 'uitnodigen' ? ids.tabInvite : ids.tabAccess}
                type="button"
                role="tab"
                className="ef-deeldialoog__tab"
                aria-selected={tab === option}
                aria-controls={option === 'uitnodigen' ? ids.panelInvite : ids.panelAccess}
                tabIndex={tab === option ? 0 : -1}
                disabled={busy && tab !== option}
                onClick={() => {
                  chooseTab(option, false)
                }}
                onKeyDown={onTabKey}
                aria-label={option === 'uitnodigen' ? undefined : accessName}
              >
                {option === 'uitnodigen' ? T.uitnodigen[language] : accessLabel}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="ef-deeldialoog__inhoud">
        {/* Altijd in de dialoog, buiten de tabbladen, zodat de melding wordt voorgelezen. */}
        <p role="status" className={status === '' ? 'ef-deeldialoog__status' : 'ef-deeldialoog__status ef-deeldialoog__bevestiging'}>
          {status}
        </p>
        {canInvite ? (
          <>
            <div id={ids.panelInvite} role="tabpanel" aria-labelledby={ids.tabInvite} hidden={tab !== 'uitnodigen'} className="ef-deeldialoog__paneel">
              <ShareInvite
                key={round}
                taal={language}
                formId={formId}
                kant={side}
                secties={props.secties}
                {...(props.advies === undefined ? {} : { advies: props.advies })}
                {...(start === undefined ? {} : { start })}
                busy={busy}
                onBusy={setBusy}
                onInvite={props.onInvite}
                firstField={firstField}
                onSent={(address, id) => {
                  setStatus(T.verstuurd[language].replace('{email}', address))
                  setNewId(id)
                  setRound((previous) => previous + 1)
                  chooseTab('toegang', true)
                }}
              />
            </div>
            <div id={ids.panelAccess} role="tabpanel" aria-labelledby={ids.tabAccess} hidden={tab !== 'toegang'} className="ef-deeldialoog__paneel">
              {list}
            </div>
          </>
        ) : (
          <div className="ef-deeldialoog__paneel">{list}</div>
        )}
      </div>
      {canInvite ? (
      <div className="ef-deeldialoog__voet">
        {tab === 'uitnodigen' ? (
          <>
            <button type="button" className="ef-actieknop" data-soort="secundair" disabled={busy} onClick={close}>
              {T.annuleren[language]}
            </button>
            <BusyButton type="submit" form={formId} className="ef-actieknop" data-soort="primair" busy={busy} busyText={T.bezig[language]}>
              {T.versturen[language]}
            </BusyButton>
          </>
        ) : (
          <button
            type="button"
            className="ef-actieknop"
            data-soort="primair"
            onClick={() => {
              chooseTab('uitnodigen', false)
              setFocusForm(true)
            }}
          >
            {T.uitnodigen[language]}
          </button>
        )}
      </div>
      ) : null}
    </dialog>
  )
}
